import {lazy, Suspense} from 'react';
import {createBrowserRouter, Navigate} from 'react-router-dom';
import RootLayout from './components/layouts/RootLayout';
import RouterErrorBoundary from './components/RouterErrorBoundary';
import Redirector from './Redirector';
import {Layout} from './types/enums/Layout';
import {ExternalUserRole, InternalUserRole, UserRole} from './types/enums/UserRole';

const AccountDeactivated = lazy(() => import('./pages/AccountDeactivated'));
const Checklist = lazy(() => import('./pages/checklist/Checklist'));
const Contact = lazy(() => import('./pages/contact/Contact'));
const DataPrivacy = lazy(() => import('./pages/data-privacy/DataPrivacy'));
const ExtensionDeadline = lazy(() => import('./pages/extension-deadline/ExtensionDeadline'));
const Imprint = lazy(() => import('./pages/footer/Imprint'));
const Help = lazy(() => import('./pages/help/Help'));
const IncidentManagement = lazy(() => import('./pages/incident-management/IncidentManagement'));
const LegalHints = lazy(() => import('./pages/legal-hints/LegalHints'));
const LiveQm = lazy(() => import('./pages/LiveQm'));
const Summary = lazy(() => import('./pages/matchSummary/Summary'));
const NoAccess = lazy(() => import('./pages/NoAccess'));
const EditProfile = lazy(() => import('./pages/profile/EditProfile'));
const CameraPersonDetailReporting = lazy(() => import('./pages/reporting/CameraPersonDetailReporting'));
const ReportingLayout = lazy(() => import('./pages/reporting/ReportingLayout'));
const StaffReporting = lazy(() => import('./pages/reporting/StaffReporting'));
const Reports = lazy(() => import('./pages/Reports'));
const ArchivedReportPage = lazy(() => import('./pages/reports/archive-reports/ArchivedReportPage'));
const ArchiveReports = lazy(() => import('./pages/reports/archive-reports/ArchiveReports'));
const MyReports = lazy(() => import('./pages/reports/my-reports/MyReports'));
const SystemSettings = lazy(() => import('./pages/system-settings/SystemSettings'));
const TermsOfUse = lazy(() => import('./pages/terms-of-use/TermsOfUse'));
const UserList = lazy(() => import('./pages/users/UserList'));

const LazyComponent = ({children}: {children: React.ReactNode}) => (
    <Suspense fallback={null}>
        {children}
    </Suspense>
);

export interface RouterProps {
    loggedIn: boolean;
    userIsActive: boolean;
    userRole?: UserRole | ExternalUserRole | InternalUserRole;
    canSeeIncidentManagement: boolean;
    canSeeArchiveReport: boolean;
    canSeeLiveQm: boolean;
    canSeeSummary: boolean;
    canSeeUsers: boolean;
    canSeeChecklist: boolean;
    canSeeReporting: boolean;
    canSeeReportsForQMResponsibles: boolean;
    canWriteSystemSettings: boolean;
    canSeeExtensionDeadline: boolean;
    canSeeReport: boolean;
    showDataPrivacyModal: boolean;
    showTermsOfUseModal: boolean;
    showLegalHintsModal: boolean;
    setShowDataPrivacyModal: (show: boolean) => void;
    setShowTermsOfUseModal: (show: boolean) => void;
    setShowLegalHintsModal: (show: boolean) => void;
    userHasAcceptedDataPrivacy: boolean;
    userHasAcceptedTermsOfUse: boolean;
    userIsRePortUser: boolean;
}

export const createAppRouter = (props: RouterProps) => {
    return createBrowserRouter([
        {
            path: '/',
            element: <RootLayout {...props} />,
            errorElement: <RouterErrorBoundary />,
            children: [
                // Public routes
                {path: 'impressum', element: <LazyComponent><Imprint /></LazyComponent>},
                {path: 'terms-of-use', element: <LazyComponent><TermsOfUse /></LazyComponent>},
                {path: 'data-privacy', element: <LazyComponent><DataPrivacy /></LazyComponent>},
                {path: 'legal-hints', element: <LazyComponent><LegalHints /></LazyComponent>},

                // Conditional access routes
                ...(props.loggedIn && !props.userIsRePortUser ? [{
                    path: '*',
                    element: <LazyComponent><NoAccess /></LazyComponent>
                }] : []),

                ...(props.loggedIn && !props.userIsActive && props.userIsRePortUser ? [
                    {index: true, element: <LazyComponent><AccountDeactivated /></LazyComponent>},
                    {path: '*', element: <LazyComponent><AccountDeactivated /></LazyComponent>}
                ] : []),

                // Protected routes
                ...(props.loggedIn && props.userIsActive ? [
                    {index: true, element: <Redirector userRole={props.userRole} />},
                    {path: 'help', element: <LazyComponent><Help /></LazyComponent>},
                    {path: 'contact', element: <LazyComponent><Contact /></LazyComponent>},
                    {path: 'live', element: <div className="content"><LazyComponent><LiveQm /></LazyComponent></div>},
                    {path: 'summary', element: <div className="content"><LazyComponent><Summary /></LazyComponent></div>},
                    ...(props.canSeeExtensionDeadline ? [{
                        path: 'extension-deadline',
                        element: <div className="content full-wide-content"><LazyComponent><ExtensionDeadline /></LazyComponent></div>
                    }] : []),
                    {path: 'reports/:reportId', element: <div className="content"><LazyComponent><Reports /></LazyComponent></div>},
                    {path: 'reports', element: <div className="content"><LazyComponent>{props.canSeeReportsForQMResponsibles ? <Reports /> : <MyReports />}</LazyComponent></div>},
                    {path: 'archive-reports/:reportId', element: <div className="content"><LazyComponent><ArchivedReportPage /></LazyComponent></div>},
                    {path: 'archive-reports', element: <div className="content full-wide-content"><LazyComponent><ArchiveReports /></LazyComponent></div>},
                    {
                        path: 'reporting',
                        element: <LazyComponent><ReportingLayout /></LazyComponent>,
                        children: [
                            {path: 'staff/:reportingModule?', element: <div className="content full-wide-content admin-content"><LazyComponent><StaffReporting /></LazyComponent></div>},
                            {path: 'staff/camera-person/:personPiid?', element: <div className="content full-wide-content admin-content"><LazyComponent><CameraPersonDetailReporting /></LazyComponent></div>},
                            {path: 'kick-off-times', element: <></>}
                        ]
                    },
                    {path: 'incident-management/list', element: <div className="content wide-content"><LazyComponent><IncidentManagement layout={Layout.LIST} /></LazyComponent></div>},
                    {path: 'incident-management/view/:incidentId?', element: <div className="content wide-content"><LazyComponent><IncidentManagement layout={Layout.FOCUSED} /></LazyComponent></div>},
                    {path: 'incident-management', element: <div className="content wide-content"><LazyComponent><IncidentManagement /></LazyComponent></div>},
                    {path: 'settings/users', element: <div className="content admin-content"><LazyComponent><UserList /></LazyComponent></div>},
                    {path: 'settings/edit-profile', element: <div className="content admin-content"><LazyComponent><EditProfile /></LazyComponent></div>},
                    ...(props.canWriteSystemSettings ? [{
                        path: 'settings/system/:systemSettingModule?/:reportType?/:categoryId?/:matchDay?/:productionStage?',
                        element: <div className="content admin-content"><LazyComponent><SystemSettings /></LazyComponent></div>
                    }] : []),
                    {path: 'checklist/:productionId/:productionStage', element: <div className="content"><LazyComponent><Checklist /></LazyComponent></div>},
                    {path: '*', element: <Navigate to="/" replace />}
                ] : [])
            ]
        }
    ]);
};
